import { useEffect, useRef } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { Paginated } from "@nova/shared";
import { api } from "@/lib/api";

export interface BellItem {
  id: string;
  kind: string;
  severity: "INFO" | "WARNING" | "CRITICAL";
  titleAr: string;
  bodyAr?: string | null;
  titleEn?: string | null;
  bodyEn?: string | null;
  link?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  isRead: boolean;
  createdAt: string;
}

export const bellKeys = {
  all: ["notifications", "bell"] as const,
  unread: ["notifications", "bell", "unread"] as const,
  list: ["notifications", "bell", "list"] as const,
};

/** How often the badge asks the server; the server is the source of truth. */
export const BELL_POLL_MS = 15_000;
const PAGE_SIZE = 15;

/**
 * The badge. There is no socket in this app (every live screen polls), so the
 * bell polls one cheap count — faster while the tab is visible, paused while it
 * is hidden — and refetches at once on focus and when the network returns.
 * When the number grows, the list and the HR request screens are refreshed so a
 * new item and its request show up without a reload.
 */
export function useUnreadCount(enabled: boolean) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: bellKeys.unread,
    queryFn: async () =>
      (await api.get<{ unread: number }>("/notifications/unread-count")).data.unread,
    enabled,
    refetchInterval: BELL_POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
  });
  const previous = useRef<number | undefined>(undefined);
  useEffect(() => {
    const next = query.data;
    if (next === undefined) return;
    if (previous.current !== undefined && next > previous.current) {
      void client.invalidateQueries({ queryKey: bellKeys.list });
      // A new HR item usually means a request changed state somewhere else.
      void client.invalidateQueries({ queryKey: ["attendance"] });
    }
    previous.current = next;
  }, [client, query.data]);
  return query;
}

export function useBellList(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: bellKeys.list,
    initialPageParam: 1,
    enabled,
    queryFn: async ({ pageParam }) =>
      (
        await api.get<Paginated<BellItem>>("/notifications", {
          params: { page: pageParam, pageSize: PAGE_SIZE, newestFirst: true },
        })
      ).data,
    getNextPageParam: (last) =>
      last.page < last.totalPages ? last.page + 1 : undefined,
  });
}

function useAfterRead() {
  const client = useQueryClient();
  return (unread: number) => {
    client.setQueryData(bellKeys.unread, unread);
    void client.invalidateQueries({ queryKey: bellKeys.list });
  };
}

export function useMarkRead() {
  const after = useAfterRead();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ unread: number }>(`/notifications/${id}/read`)).data.unread,
    onSuccess: after,
  });
}

export function useMarkAllRead() {
  const after = useAfterRead();
  return useMutation({
    mutationFn: async () =>
      (await api.post<{ unread: number }>("/notifications/read-all")).data.unread,
    onSuccess: after,
  });
}

/**
 * A record opened by deep link (from the bell, a bookmark or a refresh) clears
 * the caller's own unread items about it. Best effort: failure leaves them unread.
 */
export function useMarkEntityRead(entityId: string | undefined, ready: boolean) {
  const client = useQueryClient();
  useEffect(() => {
    if (!entityId || !ready) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.get<Paginated<BellItem>>("/notifications", {
          params: { entityId, unreadOnly: true, pageSize: 50 },
        });
        let unread: number | undefined;
        for (const item of data.data) {
          if (cancelled) return;
          unread = (await api.post<{ unread: number }>(`/notifications/${item.id}/read`)).data.unread;
        }
        if (unread !== undefined && !cancelled) {
          client.setQueryData(bellKeys.unread, unread);
          void client.invalidateQueries({ queryKey: bellKeys.list });
        }
      } catch {
        /* the bell keeps the item unread; nothing else depends on it */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, entityId, ready]);
}
